import { useEffect, useMemo, useState } from "react"
import { Button, Card, Col, Form, Input, Row, Spin, Typography, message } from "antd"
import { useNavigate } from "react-router"
import { TempLayout } from "@/layouts"
import { useUserStore } from "@/stores/user/userStore"
import { useSapReservationStore } from "@/stores/sap-reservation/sapReservationStore"
import { ISapReservationDetail, ISapReservationDto } from "@/types"
import ReservationItemsTable from "./components/ReservationItemsTable"

const createEmptyRow = (): ISapReservationDetail => ({
  product_id: "",
  qty: 1,
  uom: "",
  long_desc: "",
  wbs_code: "",
  wbs_description: "",
  cost_center_code: "",
  cost_center_name: "",
})

const SapReservationsAdd = () => {
  const navigate = useNavigate()
  const { userData } = useUserStore()
  const { createSapReservation, isLoading } = useSapReservationStore()
  const [form] = Form.useForm<ISapReservationDto>()
  const [details, setDetails] = useState<ISapReservationDetail[]>([createEmptyRow()])

  useEffect(() => {
    form.setFieldsValue({
      employee_id: userData.employee_id || "",
      fullname: userData.fullname || "",
      level: userData.job_level || "",
      department: userData.organization_name || "",
      description: "",
    })
  }, [form, userData])

  const validationMessage = useMemo(() => {
    const invalidRowIndex = details.findIndex(detail => {
      const productId = String(detail.product_id || "").trim()
      const qty = Number(detail.qty)
      const uom = String(detail.uom || "").trim()
      const longDesc = String(detail.long_desc || "").trim()
      const wbsCode = String(detail.wbs_code || "").trim()
      const costCenterCode = String(detail.cost_center_code || "").trim()
      return !productId || !Number.isFinite(qty) || qty <= 0 || !uom || !longDesc || (!wbsCode && !costCenterCode)
    })

    if (invalidRowIndex === -1) {
      return null
    }

    return `Please complete Product ID, Qty, UOM, Long Description, and at least WBS or Cost Center on row ${invalidRowIndex + 1}.`
  }, [details])

  const handleSubmit = async (values: ISapReservationDto) => {
    if (validationMessage) {
      void message.error(validationMessage)
      return
    }

    try {
      const created = await createSapReservation({
        employee_id: values.employee_id,
        fullname: values.fullname,
        level: values.level,
        department: values.department,
        sap_reservation_id: values.sap_reservation_id?.trim() || "",
        description: values.description?.trim() || "",
        details: details.map(detail => ({
          product_id: String(detail.product_id || "").trim(),
          qty: Number(detail.qty),
          uom: String(detail.uom || "").trim(),
          long_desc: String(detail.long_desc || "").trim(),
          wbs_code: String(detail.wbs_code || "").trim(),
          cost_center_code: String(detail.cost_center_code || "").trim(),
        })),
      })

      void message.success("Reservation created successfully")
      navigate(created.record_id ? `/saprsv/view/${created.record_id}` : "/saprsv/history")
    } catch (error) {
      const errorMessage = error instanceof Error && "response" in error ? (error as { response?: { data?: { message?: string } } }).response?.data?.message || "Failed to create reservation" : "Failed to create reservation"
      void message.error(errorMessage)
    }
  }

  return (
    <TempLayout title="SAP - Create new Reservation" navNavigateUrl="/saprsv/history">
      <Card>
        <Spin spinning={isLoading}>
          <Form layout="vertical" form={form} onFinish={values => void handleSubmit(values)}>
            <Typography.Title level={5}>User Information</Typography.Title>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item label="Employee ID" name="employee_id">
                  <Input disabled />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Fullname" name="fullname">
                  <Input disabled />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Level" name="level">
                  <Input disabled />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Department" name="department">
                  <Input disabled />
                </Form.Item>
              </Col>
            </Row>

            <Typography.Title level={5}>Request Details</Typography.Title>
            {/* <Form.Item label="SAP Reservation ID" name="sap_reservation_id" extra="This is optional.">
              <Input placeholder="Enter SAP Reservation ID" />
            </Form.Item> */}
            <Form.Item label="Description" name="description" extra="Optional. Add business context for this reservation request.">
              <Input.TextArea placeholder="Enter reservation description (optional)" autoSize={{ minRows: 2, maxRows: 4 }} />
            </Form.Item>

            <Typography.Title level={5}>Reservation Items</Typography.Title>
            <ReservationItemsTable items={details} editable onChange={setDetails} />

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 24 }}>
              <Button type="primary" htmlType="submit" loading={isLoading}>
                Submit Reservation
              </Button>
            </div>
          </Form>
        </Spin>
      </Card>
    </TempLayout>
  )
}

export default SapReservationsAdd
